/**
 * PDP rating + venue location row: smooth scroll targets, venue pin, mobile one-line fit.
 */
(function () {
  'use strict';

  var HEADER_OFFSET = 88;
  var MOBILE_MQ = window.matchMedia('(max-width: 767px)');
  var MIN_SCALE = 0.5;
  var fitRaf = 0;

  function smoothScrollTo(el) {
    if (!el) return;
    var top = el.getBoundingClientRect().top + window.pageYOffset - HEADER_OFFSET;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  function reviewsTarget() {
    return document.getElementById('workshop-reviews');
  }

  function locationTarget() {
    return (
      document.querySelector('[data-amc-pdp-map-section]') ||
      document.querySelector('[data-pic-section]') ||
      document.querySelector('.product-info-card')
    );
  }

  function showLocationMap(section) {
    if (!section) return;
    if (typeof section.__picSetTab === 'function') {
      section.__picSetTab('location');
    } else {
      var picTab = section.querySelector('[data-pic-tab="location"]');
      if (picTab) picTab.click();
      section.querySelectorAll('[data-pic-tab]').forEach(function (tab) {
        var on = tab.getAttribute('data-pic-tab') === 'location';
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      section.querySelectorAll('[data-pic-panel]').forEach(function (panel) {
        var on = panel.getAttribute('data-pic-panel') === 'location';
        panel.classList.toggle('is-active', on);
        if (on) panel.removeAttribute('hidden');
        else panel.setAttribute('hidden', '');
      });
      var sel = section.querySelector('[data-pic-tabs-select]');
      if (sel && sel.querySelector('option[value="location"]')) sel.value = 'location';
    }
    var flowerTab = section.querySelector('[data-tab="location"]');
    if (flowerTab) flowerTab.click();
  }

  function normalizeAddress(address) {
    return String(address || '').trim().replace(/\s*\|\s*/g, ', ');
  }

  function addressParts(address) {
    return normalizeAddress(address)
      .split(',')
      .map(function (p) {
        return String(p || '').trim();
      })
      .filter(Boolean);
  }

  function looksLikeStateZip(part) {
    var p = String(part || '').trim();
    if (/^[A-Z]{2}(\s+\d{5}(-\d{4})?)?\b/i.test(p)) return true;
    if (/^\d{5}(-\d{4})?\b/.test(p)) return true;
    if (/^[A-Z]{2}$/i.test(p)) return true;
    return false;
  }

  function looksLikeCountry(part) {
    var p = String(part || '').trim().toUpperCase();
    return p === 'USA' || p === 'US' || p === 'UNITED STATES';
  }

  /** City from comma-separated address (e.g. "3615 FM691, Denison, TX…" → Denison). */
  function extractCityFromAddress(address, fallbackCity) {
    var parts = addressParts(address);
    if (parts.length < 2) return String(fallbackCity || '').trim();

    for (var i = 1; i < parts.length; i++) {
      var part = parts[i];
      if (looksLikeCountry(part) || looksLikeStateZip(part)) continue;
      if (i + 1 < parts.length && looksLikeStateZip(parts[i + 1])) return part;
      if (i === 1) return part;
    }
    return String(fallbackCity || '').trim();
  }

  function appendCity(label, city) {
    var base = String(label || '').trim();
    var c = String(city || '').trim();
    if (!base) return c;
    if (!c) return base;
    if (base.toLowerCase().indexOf(c.toLowerCase()) !== -1) return base;
    return base + ', ' + c;
  }

  /**
   * Prefer a leading place name in the address ("Radius Hotel, 15 Pitt St…"),
   * otherwise street line + city from the address.
   */
  function extractPlaceFromAddress(address) {
    var addr = String(address || '').trim();
    if (!addr) return '';

    // Normalize "Place | 100 Street" → comma form for parsing
    addr = addr.replace(/\s*\|\s*/g, ', ');

    var parts = addr.split(',').map(function (p) {
      return String(p || '').trim();
    }).filter(Boolean);

    if (parts.length >= 2) {
      var first = parts[0];
      var second = parts[1];
      // "Radius Hotel, 15 Pitt St, City"
      if (first && !/^\d/.test(first) && /^\d/.test(second)) {
        return first;
      }
      // "Radius Hotel, Suite 2, 15 Pitt St" — place then non-street then street
      if (
        first &&
        !/^\d/.test(first) &&
        parts.length >= 3 &&
        /^\d/.test(parts[2]) &&
        !/^\d/.test(second)
      ) {
        return first;
      }
    }

    // "SHERATON WESTPORT PLAZA 900 Westport Plaza Drive, St. Louis, MO"
    var m = addr.match(
      /^([A-Za-z][A-Za-z0-9 &'’.\-]{1,60}?)\s+(\d{1,6}\s+[A-Za-z].+)$/
    );
    if (m) {
      var maybePlace = String(m[1] || '').trim();
      var rest = String(m[2] || '').trim();
      if (
        maybePlace &&
        rest &&
        !/^(suite|unit|apt|apartment|floor|level|bldg|building)\b/i.test(maybePlace)
      ) {
        return maybePlace;
      }
    }

    return '';
  }

  function stateCodeFromPart(part) {
    var raw = String(part || '').trim();
    /* US: TX 75001 — CA: ON L4K 1Z8 */
    var m = raw.match(/^([A-Za-z]{2})(?:\s+(?:\d{5}(?:-\d{4})?|[A-Za-z]\d[A-Za-z]\s*\d[A-Za-z]\d))?$/);
    if (!m) return '';
    var code = m[1].toUpperCase();
    if (code === 'US' || code === 'CA') return '';
    return code;
  }

  function formatLocationLabel(detail) {
    if (!detail) return '';

    var address = String(detail.address || '').trim();
    /* Prefer place + city + region from address (not city-only venue title). */
    var parts = addressParts(address);
    var state = '';
    var cityFromAddress = '';
    for (var i = 0; i < parts.length; i++) {
      var code = stateCodeFromPart(parts[i]);
      if (!code) continue;
      state = code;
      if (i > 0) cityFromAddress = parts[i - 1];
      break;
    }
    var city = cityFromAddress || String(detail.city || '').trim();
    if (!state) {
      var fromDetail = String(detail.state || detail.state_code || detail.region_code || '').trim();
      if (/^[A-Za-z]{2}$/.test(fromDetail) && fromDetail.toUpperCase() !== 'US' && fromDetail.toUpperCase() !== 'CA') {
        state = fromDetail.toUpperCase();
      }
    }
    var place = extractPlaceFromAddress(address);
    if (place && city && place.toLowerCase() === city.toLowerCase()) place = '';

    var bits = [];
    if (place) bits.push(place);
    if (city) bits.push(city);
    if (state) bits.push(state);
    if (bits.length) return bits.join(', ');

    return String(detail.title || '').trim();
  }

  function fitRatingRow(row) {
    if (!row) return;
    row.style.transform = '';
    row.style.marginBottom = '';
    /* Long venue labels wrap; do not scale the row onto one line. */
    return;
  }

  function fitAllRatingRows(scope) {
    (scope || document).querySelectorAll('[data-amc-pdp-rating-row]').forEach(fitRatingRow);
  }

  function scheduleFit(scope) {
    window.cancelAnimationFrame(fitRaf);
    fitRaf = window.requestAnimationFrame(function () {
      fitAllRatingRows(scope);
    });
  }

  function applyLocation(detail) {
    var label = formatLocationLabel(detail);
    document.querySelectorAll('[data-amc-pdp-location]').forEach(function (btn) {
      var textEl = btn.querySelector('[data-amc-pdp-location-text]');
      if (!label) {
        btn.hidden = true;
        if (textEl) textEl.textContent = '';
        return;
      }
      if (textEl) textEl.textContent = label;
      btn.hidden = false;
      btn.setAttribute('aria-label', 'Event location: ' + label + '. View map');
    });
    scheduleFit();
  }

  function clearLocation() {
    applyLocation(null);
  }

  function bindScroll(scope) {
    (scope || document).querySelectorAll('[data-amc-scroll-to-reviews]').forEach(function (btn) {
      if (btn.dataset.amcScrollBound === '1') return;
      btn.dataset.amcScrollBound = '1';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        smoothScrollTo(reviewsTarget());
      });
    });
    (scope || document).querySelectorAll('[data-amc-scroll-to-location]').forEach(function (btn) {
      if (btn.dataset.amcScrollBound === '1') return;
      btn.dataset.amcScrollBound = '1';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var target = locationTarget();
        showLocationMap(target);
        smoothScrollTo(target);
      });
    });
  }

  function observeRows(scope) {
    (scope || document).querySelectorAll('[data-amc-pdp-rating-row]').forEach(function (row) {
      if (row.dataset.amcFitBound === '1') return;
      row.dataset.amcFitBound = '1';
      if (typeof ResizeObserver === 'undefined') return;
      var parent = row.parentElement;
      if (!parent) return;
      var ro = new ResizeObserver(function () {
        scheduleFit(scope);
      });
      ro.observe(parent);
      ro.observe(row);
    });
  }

  document.addEventListener('wbw:venue-selected', function (e) {
    applyLocation(e && e.detail);
  });
  document.addEventListener('wbw:venue-reset', clearLocation);
  document.addEventListener('amc:google-reviews-summary', function () {
    scheduleFit();
  });

  function hydrateLocationFromWizard(scope) {
    var root =
      (scope || document).querySelector('[data-popup-city-wizard]') ||
      (scope || document).querySelector('[data-amc-shop-wizard]');
    if (!root) return;
    var title = String(root.getAttribute('data-venue-name') || '').trim();
    var address = String(
      root.getAttribute('data-venue-address-full') || root.getAttribute('data-venue-address') || ''
    ).trim();
    if (!title && !address) return;
    applyLocation({ title: title, address: address });
  }

  function boot(scope) {
    bindScroll(scope);
    observeRows(scope);
    hydrateLocationFromWizard(scope);
    scheduleFit(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot();
    });
  } else {
    boot();
  }

  window.addEventListener('resize', function () {
    scheduleFit();
  });

  if (typeof MOBILE_MQ.addEventListener === 'function') {
    MOBILE_MQ.addEventListener('change', function () {
      scheduleFit();
    });
  } else if (typeof MOBILE_MQ.addListener === 'function') {
    MOBILE_MQ.addListener(function () {
      scheduleFit();
    });
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target) boot(e.target);
  });
})();
